import { lookup, renderDynamic } from '@npc/copy'

import type {
  AiOsEnvironment,
  AiOsEvaluationOutcome,
  AiOsLearningState,
  AiOsModuleState,
  AiOsProposalState,
  AiOsRisk,
  AiOsStopReason,
  AiOsVerification,
  ImprovementProposal,
  ModuleEvaluationRun,
  VerifiedLearningEntry,
} from './ai-os'

/**
 * 模块演进的**判据**：枚举全集、主链顺序、状态机邻接表、以及几条读出来的判断。
 *
 * 这是 `packages/shared-go/moduleevolution` 的前端一半。那个包在 Go 那侧，
 * 前端够不着，所以这里重抄了一份——**而「重抄一份」在这个仓库里是有前科的**
 * （CLAUDE.md §8「同一条规则有两份实现」）。下面三条是这份副本的安全带：
 *
 * # 一、枚举全集由编译期哨兵盯着，不靠人记得
 *
 * 每一组枚举下面都有一对 `Assert<Exclude<...>>`。它们做的是**逐字集合相等**，
 * 两个方向都验：契约里多一档、这里少一档，`tsc` 当场红；这里多一档、
 * 契约里没有，也红。判据取自 `packages/api-client` 的生成物——
 * 也就是 `openapi.yaml` 本身，不是取自我对它的印象
 * （CLAUDE.md §8：判据与用例同源的话，两边永远对得上，而那只证明我没写错字）。
 *
 * 包含式的断言在这里是不够的：**枚举漂移恰恰长在多出来的那一侧。**
 *
 * # 二、邻接表是第三份真相，而且它没有哨兵
 *
 * 库的 CHECK、Go 的 `transitions`、这里的 `TRANSITIONS`——**三份**。
 * 前两份由 `tests/db` 对账，这一份没有任何东西对得上：OpenAPI 不带转移表。
 *
 * 交付这一版时做过一次**跨源核对**（不是自洽核对）：把 Go 那侧的
 * `NextStates` 逐档跑出来打成 JSON，与这里的 `nextStates` 逐字比，十四档
 * 连**顺序**都一样。判据取自 Go 代码的运行结果，不是取自我读它的印象——
 * CLAUDE.md §8：判据与用例同源的话两边永远对得上，而那只证明我没写错字。
 *
 * **那次核对是一次性的，不是常驻检查。** 常驻要么把 Go 工具链拖进前端用例
 * （`make test` 从此依赖 go run），要么把那张 JSON 提交进来当基线（那就成了
 * 第四份真相）。两条都比问题本身贵。真正的了结是契约把它暴露出来，见上一段。
 *
 * 所以它只做**只读的解释**（这条提案还能往哪儿走、是不是走到头了），
 * **一处都不用来放行任何写操作**——写操作只有一个，见下面第三条。
 * 哪天契约把转移表暴露出来（`next_states` 字段，或者一个 TS 版的
 * moduleevolution），这个文件里的表就该删掉，别留着。
 *
 * # 三、`production_verified` **刻意没有前端实现**
 *
 * Go 那侧有 `SupportsVerification(v, env, outcome, finished)`。
 * **这里故意不抄它。**
 *
 * 抄过来的那一刻，前端就有了一条自己算出「这东西在生产上验过了」的路，
 * 而那个结论的唯一合法来源是库里那条
 * `module_evaluation_runs_production_verified_needs_pass`。
 * 一个能自己算出这个结论的界面，和一个伪造它的界面，从代码上分不出来。
 *
 * 所以这里只有 {@link isProductionVerified}，它做的事只有一件：
 * **读响应里的那个字段**。
 */

// ---------------------------------------------------------------- 编译期哨兵

/** `Exclude<...>` 的结果不是 `never` 时，这一行编不过。 */
type Assert<T extends never> = T

// ---------------------------------------------------------------- 风险分级

export const ALL_RISKS = ['green', 'amber', 'red'] as const satisfies readonly AiOsRisk[]

export type _RiskCoversContract = Assert<Exclude<AiOsRisk, (typeof ALL_RISKS)[number]>>
export type _RiskAddsNothing = Assert<Exclude<(typeof ALL_RISKS)[number], AiOsRisk>>

/**
 * 顺序即严重度。**不导出这张数字表**——把「绿 < 黄 < 红」暴露成数字，
 * 下一个人就会拿它做算术（平均风险、风险总分），而风险不是可加的量。
 * 与 Go 那侧 `severity` 不导出是同一条理由。
 */
const RISK_SEVERITY: Record<AiOsRisk, number> = { green: 0, amber: 1, red: 2 }

/** `risk` 达到或超过 `floor` 了吗。别自己比字符串。 */
export function riskAtLeast(risk: AiOsRisk, floor: AiOsRisk): boolean {
  return RISK_SEVERITY[risk] >= RISK_SEVERITY[floor]
}

/**
 * 这一档**是不是必须人点**。
 *
 * 注意它只是下界，不是判据本身：黄级回 `false`，而一条改共享合同的黄级提案
 * 完全可以要求人点。库里那条 CHECK 写的是「红 ⇒ 必须点」而不是「红 ⟺ 必须点」，
 * 这里保持同一个语义。**要判一条提案，用 {@link awaitingHumanClick}。**
 */
export function riskRequiresHumanClick(risk: AiOsRisk): boolean {
  return risk === 'red'
}

// ---------------------------------------------------------------- 证据等级

export const ALL_VERIFICATIONS = [
  'candidate',
  'preview_verified',
  'production_verified',
] as const satisfies readonly AiOsVerification[]

export type _VerificationCoversContract = Assert<
  Exclude<AiOsVerification, (typeof ALL_VERIFICATIONS)[number]>
>
export type _VerificationAddsNothing = Assert<
  Exclude<(typeof ALL_VERIFICATIONS)[number], AiOsVerification>
>

export const ALL_ENVIRONMENTS = [
  'isolated',
  'preview',
  'production',
] as const satisfies readonly AiOsEnvironment[]

export type _EnvironmentCoversContract = Assert<
  Exclude<AiOsEnvironment, (typeof ALL_ENVIRONMENTS)[number]>
>
export type _EnvironmentAddsNothing = Assert<
  Exclude<(typeof ALL_ENVIRONMENTS)[number], AiOsEnvironment>
>

export const ALL_EVALUATION_OUTCOMES = [
  'pending',
  'passed',
  'failed',
  'degraded',
] as const satisfies readonly AiOsEvaluationOutcome[]

export type _OutcomeCoversContract = Assert<
  Exclude<AiOsEvaluationOutcome, (typeof ALL_EVALUATION_OUTCOMES)[number]>
>
export type _OutcomeAddsNothing = Assert<
  Exclude<(typeof ALL_EVALUATION_OUTCOMES)[number], AiOsEvaluationOutcome>
>

/**
 * 这次评估是不是「在生产上验过了」。
 *
 * **它只读一个字段，一处推导都没有**，理由写在文件头第三条。
 * 想加一个「env 是 production 而且 outcome 是 passed 所以它其实验过了」的分支——
 * 那正是这个函数存在要挡住的事。
 */
export function isProductionVerified(run: ModuleEvaluationRun): boolean {
  return run.verification === 'production_verified'
}

// ---------------------------------------------------------------- 模块 / 学习项

export const ALL_MODULE_STATES = [
  'active',
  'deprecated',
  'retired',
] as const satisfies readonly AiOsModuleState[]

export type _ModuleStateCoversContract = Assert<
  Exclude<AiOsModuleState, (typeof ALL_MODULE_STATES)[number]>
>
export type _ModuleStateAddsNothing = Assert<
  Exclude<(typeof ALL_MODULE_STATES)[number], AiOsModuleState>
>

export const ALL_LEARNING_STATES = [
  'candidate',
  'promoted',
  'disabled',
  'expired',
  'rolled_back',
] as const satisfies readonly AiOsLearningState[]

export type _LearningStateCoversContract = Assert<
  Exclude<AiOsLearningState, (typeof ALL_LEARNING_STATES)[number]>
>
export type _LearningStateAddsNothing = Assert<
  Exclude<(typeof ALL_LEARNING_STATES)[number], AiOsLearningState>
>

// ---------------------------------------------------------------- 提案状态机

export const ALL_PROPOSAL_STATES = [
  'observed',
  'diagnosed',
  'proposed',
  'accepted',
  'executing',
  'preview_verified',
  'releasing',
  'production_verified',
  'learning_candidate',
  'promoted',
  'failed',
  'fix_requested',
  'rollback_requested',
  'rolled_back',
] as const satisfies readonly AiOsProposalState[]

export type _ProposalStateCoversContract = Assert<
  Exclude<AiOsProposalState, (typeof ALL_PROPOSAL_STATES)[number]>
>
export type _ProposalStateAddsNothing = Assert<
  Exclude<(typeof ALL_PROPOSAL_STATES)[number], AiOsProposalState>
>

/**
 * 蓝图 §4 那条主链，按顺序。**只能一格一格往前，不许跳级**——
 * 跳级正是 §4 点名的那几种「不得当作完成」：CI 绿就当发布过了、
 * 部署接口回 accepted 就当生产验过了。
 */
export const MAINLINE = [
  'observed',
  'diagnosed',
  'proposed',
  'accepted',
  'executing',
  'preview_verified',
  'releasing',
  'production_verified',
  'learning_candidate',
  'promoted',
] as const satisfies readonly AiOsProposalState[]

/** 四个不在主链上的。合起来必须正好是全集，`module-evolution.test.ts` 验这一点。 */
export const OFF_MAINLINE = [
  'failed',
  'fix_requested',
  'rollback_requested',
  'rolled_back',
] as const satisfies readonly AiOsProposalState[]

/** 这一档在主链上排第几（从 0 起）。不在主链上回 `-1`。 */
export function mainlineIndex(state: AiOsProposalState): number {
  return (MAINLINE as readonly AiOsProposalState[]).indexOf(state)
}

/**
 * 邻接表。与 `packages/shared-go/moduleevolution` 的 `transitions` 逐条对应。
 *
 * 建表的方式刻意跟 Go 那边一样是**推出来的而不是抄出来的**：主链由
 * {@link MAINLINE} 生成，几组附加边由下面那几个循环加。
 * 一条一条列出来的话，改主链顺序时会漏掉其中一格，而那种漏掉不会有任何提示。
 */
const TRANSITIONS: Record<AiOsProposalState, Set<AiOsProposalState>> = (() => {
  const t = Object.fromEntries(
    ALL_PROPOSAL_STATES.map((s) => [s, new Set<AiOsProposalState>()]),
  ) as Record<AiOsProposalState, Set<AiOsProposalState>>

  // 主链：一格一格往前。
  MAINLINE.forEach((from, i) => {
    const to = MAINLINE[i + 1]
    if (to !== undefined) t[from].add(to)
  })

  // 任意一层失败都进 failed（§4）。**已经晋升的除外**——它没有「这一层」了。
  for (const s of MAINLINE) {
    if (s === 'promoted') continue
    t[s].add('failed')
  }

  // fix_requested 只对「已经开工」的那几档有意义。还没接受的提案谈不上返修。
  for (const s of ['executing', 'preview_verified', 'releasing', 'production_verified'] as const) {
    t[s].add('fix_requested')
  }
  // 返修完回到 executing，由原 owner 接着走。
  t.fix_requested.add('executing')
  t.fix_requested.add('failed')

  // 生产指标恶化 → rollback_requested → rolled_back（§4 最后一句）。
  // **只有真的碰过生产的那几档才谈得上回滚**：还没发布的东西没有可回滚的副作用。
  for (const s of [
    'releasing',
    'production_verified',
    'learning_candidate',
    'promoted',
  ] as const) {
    t[s].add('rollback_requested')
  }
  t.rollback_requested.add('rolled_back')
  t.rollback_requested.add('failed')

  return t
})()

/**
 * `from → to` 合法吗。
 *
 * 相同状态回 `false`——一次「什么都没变」的跃迁不是幂等，
 * 是一个说不出改变了什么的写操作（CLAUDE.md §8）。
 *
 * **这个函数在这份代码里没有任何写操作的调用方，而且不该有。**
 * 冻结合同这一组是八读三写，三个写口都不执行任何东西（登记提案、退回重走、
 * 关掉一条经验）；界面上没有任何按钮会让状态自己往前走。
 */
export function canTransition(from: AiOsProposalState, to: AiOsProposalState): boolean {
  return TRANSITIONS[from].has(to)
}

/** `from` 的全部合法后继，按 {@link ALL_PROPOSAL_STATES} 的顺序。 */
export function nextStates(from: AiOsProposalState): AiOsProposalState[] {
  return ALL_PROPOSAL_STATES.filter((s) => TRANSITIONS[from].has(s))
}

/** 走到头了：再没有任何合法后继。 */
export function isTerminal(state: AiOsProposalState): boolean {
  return TRANSITIONS[state].size === 0
}

/**
 * 这一档是不是必须已经挂上开发任务。
 *
 * §3.4：被接受的提案通过现有 development task 流程执行。走到 `executing`
 * 还没有开发任务，意味着有人绕开了那条流程——而绕开它等于没有 PR、没有 CI、没有独审。
 * 与迁移 000079 的 `improvement_proposals_executing_has_task` 是同一条规则。
 */
export function requiresDevelopmentTask(state: AiOsProposalState): boolean {
  return mainlineIndex(state) >= mainlineIndex('executing')
}

/**
 * 这一档在人还没点之前是不是走不了。
 * 与迁移里的 `improvement_proposals_click_before_execution` 是同一条规则。
 */
export function blockedWithoutHumanClick(state: AiOsProposalState): boolean {
  switch (state) {
    case 'observed':
    case 'diagnosed':
    case 'proposed':
    case 'failed':
    case 'rollback_requested':
    case 'rolled_back':
      return false
    default:
      return true
  }
}

// ---------------------------------------------------------------- 读出来的判断

/**
 * 这条提案**现在正等着人点头**吗。
 *
 * 判据是响应里那两个字段，不是风险等级：`requires_human_click` 为真而
 * `human_clicked_at` 还空着。用风险等级去判会漏掉「黄级也要求人点」那一档，
 * 而那一档存在的理由，正是库里那条 CHECK 写成「红 ⇒ 必须点」
 * 而不是「红 ⟺ 必须点」。
 */
export function awaitingHumanClick(proposal: ImprovementProposal): boolean {
  return proposal.requires_human_click && !proposal.human_clicked_at
}

/**
 * 这条提案**已经被人点过头**了吗。
 *
 * 与上面那条不是取反：一条根本不要求人点的提案，两条都回 `false`。
 * 「不需要点」和「点过了」是两件事，合成一句会让用户以为自己点过了。
 */
export function humanClickDone(proposal: ImprovementProposal): boolean {
  return proposal.requires_human_click && Boolean(proposal.human_clicked_at)
}


// ---------------------------------------------------------------- 停止原因

export const ALL_STOP_REASONS = [
  'budget_exhausted',
  'stop_condition_met',
  'evaluation_failed',
  'negative_case_failed',
  'retry_exhausted',
  'blocked_by_red',
  'boundary_violation',
  'module_gone',
] as const satisfies readonly AiOsStopReason[]

export type _StopReasonCoversContract = Assert<
  Exclude<AiOsStopReason, (typeof ALL_STOP_REASONS)[number]>
>
export type _StopReasonAddsNothing = Assert<
  Exclude<(typeof ALL_STOP_REASONS)[number], AiOsStopReason>
>

/**
 * 「为什么停下来」那一句。**前端不做 stop_reason → 模板的映射。**
 *
 * 契约把这件事说死了：服务端从生成的常量里填 `stop_reason_copy_id`，前端照着取。
 * 那张表在 Go 那侧由 `AssertStopReasonCoverage()` 在**起服务时**就验过一遍——
 * 前端再抄一份就是第三份真相，而它没有任何东西对账。
 *
 * # 三道 fail closed，任一不过就整句不显示
 *
 * 1. **没给 copy id** → 不显示
 * 2. **给的 id 查不到模板**（版本错位、模板改名、后端填错）→ 不显示
 * 3. **模板要变量而这边填不出来** → 不显示
 *
 * 第三道不是多余的：八档里有三档带变量（`ai_os.retry.gave_up {attempt}`、
 * `ai_os.eval.negative_cases_failed {count}`、`ai_os.risk.over_module_cap {name}`），
 * 填不出来时 `renderDynamic` 会失败，而**失败要在这儿被吞掉，不是抛到界面上**。
 *
 * # 两件绝不做的事
 *
 *   编一句话顶上     那是凭空造一个平台没说过的结论
 *   把枚举名印出来   `budget_exhausted` 六个字母对用户什么都没说，
 *                    而它看起来像是「界面坏了」
 *
 * 少一句解释好过多一句错话：旁边 `stage_label`（「没做成，我停下了」）本来就在，
 * 用户不会看到一片空白。
 */
export function stopReasonSentence(
  proposal: ImprovementProposal,
  facts: { moduleName?: string } = {},
): string | null {
  const id = proposal.stop_reason_copy_id
  if (!id) return null

  const template = lookup(id)
  if (template === undefined) return null

  // 只填这条模板**真的要**的那几个变量。多填一个 `renderDynamic` 会拒
  // （「多了一个变量」也是一种错），所以按它声明的名字逐个取
  const bag = stopFactBag(proposal, facts)
  const vars: Record<string, string> = {}
  for (const name of template.vars) {
    const value = bag[name]
    // **填不出来就整句不显示。** 硬塞一个空串会渲染出一句缺字的话，
    // 而那比不显示更难看出是坏的
    if (value === undefined) return null
    vars[name] = value
  }

  const result = renderDynamic(id, vars)
  return result.ok ? result.text : null
}

/** 八档模板可能要的那几个变量，能填出来的才进袋子。 */
function stopFactBag(
  proposal: ImprovementProposal,
  facts: { moduleName?: string },
): Record<string, string | undefined> {
  const stop = (proposal.stop_facts ?? {}) as Record<string, unknown>
  const count = stop.negative_case_count
  return {
    // 「试了 N 次都没成」——N 是提案上那个 retry_count，不是猜的
    attempt: typeof proposal.retry_count === 'number' ? String(proposal.retry_count) : undefined,
    count: typeof count === 'number' ? String(count) : undefined,
    name: facts.moduleName,
  }
}

/**
 * `budget_exhausted` 那一档的两个数。
 *
 * 契约保证这一档**必定带** `budget_limit` 与 `budget_consumed`（库里 CHECK 兜着）。
 * 但「契约保证」不等于「这次一定有」——版本错位、后端填错都可能让它缺，
 * 所以两个都在且都是数字才回，缺一个整块不显示。
 * **半截的对比（「超了 undefined」）比不显示更糟。**
 */
export function budgetFacts(
  proposal: ImprovementProposal,
): { limit: number; consumed: number } | null {
  if (proposal.stop_reason !== 'budget_exhausted') return null
  const facts = proposal.stop_facts
  if (!facts) return null
  const limit = (facts as Record<string, unknown>).budget_limit
  const consumed = (facts as Record<string, unknown>).budget_consumed
  if (typeof limit !== 'number' || typeof consumed !== 'number') return null
  return { limit, consumed }
}

// ---------------------------------------------------------------- 重试

/**
 * 这一档能不能重试。
 *
 * 契约：**正在跑的那几档不许重试**——对一件还在跑的活再发一次重试，
 * 等于同一件事跑两遍。能重试的是已经停下来的那两档。
 *
 * 界面拿它决定**要不要显示那个按钮**。服务端仍然会自己判一遍（409
 * `not_retryable`）——这里不是权限判断，是「别给一个点了必然失败的按钮」。
 */
export function isRetryable(state: AiOsProposalState): boolean {
  return state === 'failed' || state === 'fix_requested'
}

/**
 * 红级重试卡在「还没人点」上吗。
 *
 * 三条一起成立才算卡住：**红级** + **停在 blocked_by_red** + **那个 Action 还没批**。
 *
 * 契约给了 `approval.approved`，而那一项**是服务端验过才填的**——
 * 库里查不到它（`actions` 表没有 `account_id`，账号那一维钉不住）。
 * 所以这里只读它，不自己推：把「有 action_id」读成「批过了」，
 * 等于让界面替服务端做了一次它做不了的判断。
 *
 * 没有 `approval` 时回 `true`（卡住）——**fail closed**：
 * 说不出批没批，就当没批。反过来默认放行的话，一次字段缺失会变成一次越权重试。
 */
export function blockedByRed(proposal: ImprovementProposal): boolean {
  if (proposal.stop_reason !== 'blocked_by_red') return false
  return proposal.approval?.approved !== true
}

/**
 * 现在可以点那个「再来一次」吗。
 *
 * **红级没批过就不许点**——这是界面这一侧的门，服务端那侧还有一道
 * （409 `red_needs_approval`）。两道都要：只有服务端那道的话，
 * 用户会点一下、等一会儿、然后收到一句他本来就该提前知道的拒绝。
 */
export function canRetryNow(proposal: ImprovementProposal): boolean {
  return isRetryable(proposal.state) && !blockedByRed(proposal)
}

/**
 * 这条经验能不能关。
 *
 * 契约：**已经回退掉的关不了**（409 `not_disableable`）。
 * 已经关掉的也不用再关一次——那不是错误，只是没有意义。
 */
export function canDisableLearning(entry: VerifiedLearningEntry): boolean {
  return entry.state !== 'rolled_back' && entry.state !== 'disabled'
}

/**
 * 「谁在什么时候点的」这一句凑得齐吗。
 *
 * 契约说 `human_clicked_at` 与 `human_clicked_by` **成对**（数据库两侧都挡）。
 * 但界面不拿「契约说成对」当成「这次一定成对」：少一样就整句不显示——
 * 一句「在 2026-08-20 点过了」而说不出是谁，在事后追责时
 * 与「有人偷偷点的」无法区分。
 */
export function clickedBy(
  proposal: ImprovementProposal,
): { who: string; when: string } | null {
  const who = proposal.human_clicked_by
  const when = proposal.human_clicked_at
  if (!who || !when) return null
  return { who, when }
}
